export interface SessionConfig {
  enabled: boolean;
  timeoutMinutes: number;
  warningMinutes: number;
}

const DEFAULT_TIMEOUT_MINUTES = 30;
const DEFAULT_WARNING_MINUTES = 5;
/** Never allow idle timeout below 5 minutes — prevents accidental 1-minute logouts from misconfiguration. */
const MIN_TIMEOUT_MINUTES = 5;

function parsePositiveInt(value: string | undefined, fallback: number): number {
  const parsed = parseInt(String(value ?? '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const getSessionConfig = (): SessionConfig => {
  const enabled = import.meta.env.VITE_SESSION_TIMEOUT_ENABLED === 'true';
  let timeoutMinutes = parsePositiveInt(
    import.meta.env.VITE_SESSION_TIMEOUT_MINUTES,
    DEFAULT_TIMEOUT_MINUTES,
  );
  let warningMinutes = parsePositiveInt(
    import.meta.env.VITE_SESSION_WARNING_MINUTES,
    DEFAULT_WARNING_MINUTES,
  );

  if (enabled && timeoutMinutes < MIN_TIMEOUT_MINUTES) {
    console.warn(
      `Session timeout: minimum is ${MIN_TIMEOUT_MINUTES} minutes (got ${timeoutMinutes}). ` +
        `Using ${MIN_TIMEOUT_MINUTES} minutes.`,
    );
    timeoutMinutes = MIN_TIMEOUT_MINUTES;
  }

  if (warningMinutes >= timeoutMinutes) {
    console.warn(
      'Session timeout: warning minutes must be less than timeout minutes. ' +
        `Using ${DEFAULT_WARNING_MINUTES} minute warning before ${timeoutMinutes} minute timeout.`,
    );
    warningMinutes = Math.max(1, timeoutMinutes - DEFAULT_WARNING_MINUTES);
  }

  return {
    enabled,
    timeoutMinutes,
    warningMinutes,
  };
};

/** Merge env defaults with per-user session_preferences from the database. */
export function resolveSessionConfig(
  sessionPrefs?: {
    timeout_enabled?: boolean;
    timeout_minutes?: number;
    warning_minutes?: number;
  } | null,
): SessionConfig {
  const env = getSessionConfig();
  if (!sessionPrefs) return env;

  let timeoutMinutes = parsePositiveInt(
    String(sessionPrefs.timeout_minutes ?? ''),
    env.timeoutMinutes,
  );
  let warningMinutes = parsePositiveInt(
    String(sessionPrefs.warning_minutes ?? ''),
    env.warningMinutes,
  );
  const enabled =
    typeof sessionPrefs.timeout_enabled === 'boolean'
      ? sessionPrefs.timeout_enabled
      : env.enabled;

  if (enabled && timeoutMinutes < MIN_TIMEOUT_MINUTES) {
    timeoutMinutes = MIN_TIMEOUT_MINUTES;
  }
  if (warningMinutes >= timeoutMinutes) {
    warningMinutes = Math.max(1, timeoutMinutes - DEFAULT_WARNING_MINUTES);
  }

  return { enabled, timeoutMinutes, warningMinutes };
}

// Default configuration for development
export const defaultSessionConfig: SessionConfig = {
  enabled: false,
  timeoutMinutes: DEFAULT_TIMEOUT_MINUTES,
  warningMinutes: DEFAULT_WARNING_MINUTES,
};
